import {dateToStr, formatWith} from './delphiFormat.js'

/**
 * Run-time text for the Report view: headings, subheadings, group headers and
 * footers, export title rows.
 *
 * FrameTracker builds these in code (lblHead.Caption := 'W/O Pending Report from '
 * + DateToStr(d1) + ...), so the registry describes the same string building as a
 * template: a string, or a list of parts each with an optional `when`.
 *
 *   [
 *     {text: 'W/O Pending Report from {date_from} to {date_to}'},
 *     {text: ' - Location {loc:label}', when: 'loc'},
 *     {text: ' - Shipvia {shipvia}', when: {shipvia: ['UPS', 'FEDEX']}},
 *   ]
 *
 * Placeholders ({name} is always a parameter of the run; reserved names start
 * with '_'):
 *   {param}         the run's value of a parameter. A date prints as DateToStr
 *                   (m/d/yyyy); anything else as sent.
 *   {param:value}   the value as sent, a date included.
 *   {param:label}   the chosen option's label (a picker or radio).
 *   {param:field}   any other field of the chosen option, e.g. {loc:name}.
 *   {row:Col}       the group's first row (in a heading: the result's first
 *                   row), formatted like the column.
 *   {_count}        how many rows (group or report), as '#,0'.
 *   {_sum:Col}      the sum of a column over those rows (footers), formatted
 *                   with its footerFormat, else totalFormat, else format.
 *   {_scope:sls}    the sales code the run was scoped to (response meta.scope).
 *   {_meta:key}     any value from the response's meta (dot path allowed).
 *   {_user}         the signed-in user's login.
 *   {param:sqlList} a multiple pick as FrameTracker's SQL list: ('B','C','E').
 * An unknown placeholder prints nothing.
 *
 * A part's `when` is a parameter name (true when the run sent it, i.e. it was
 * not blank) or {param: [values]} (true when the run's value is one of them;
 * every key must match). `whenBlank: 'param'` is true when that parameter was
 * blank ("All Locations"). Both accept the reserved names '_scope:sls' and
 * '_meta:key' (dot paths allowed) as well as parameter names. `whenEqual` and
 * `whenNotEqual` take two names and compare their values.
 *
 * Pure: checked by scripts/report-checks.mjs.
 */

/**
 * The value a condition names: a run parameter, or a reserved name
 * ('_scope:sls' is meta.scope.sls, '_meta:key' is meta.key).
 */
function conditionValue(name, params, meta) {
    const str = String(name)
    if (str.indexOf('_scope:') === 0) return pathValue(meta, 'scope.' + str.slice(7))
    if (str.indexOf('_meta:') === 0) return pathValue(meta, str.slice(6))
    return (params || {})[str]
}

/**
 * The values a multiple param was sent with: the joined string split again on
 * its separator ('' splits 'BCE' into letters). Null for a single param.
 */
function multipleValues(name, value, paramDefs) {
    const def = (paramDefs || {})[name]
    if (!def || !def.multiple || !isSet(value)) return null
    const join = def.join !== undefined && def.join !== null ? def.join : ','
    return String(value).split(join).filter(v => v !== '')
}

/**
 * Whether a {text, when, whenBlank} part applies to this run. `meta` is the run
 * response's meta, for reserved names.
 */
export function partApplies(part, params, meta = {}, paramDefs = {}) {
    if (part.whenBlank !== undefined && part.whenBlank !== null && part.whenBlank !== '') {
        const names = Array.isArray(part.whenBlank) ? part.whenBlank : [part.whenBlank]
        if (names.some(name => isSet(conditionValue(name, params, meta)))) return false
    }
    // whenEqual / whenNotEqual: [a, b], two run values compared as text (Daily
    // Activity's 'for <date>' against 'from <d1> to <d2>').
    const pair = (names, equal) => {
        if (!Array.isArray(names) || names.length < 2) return true
        const a = conditionValue(names[0], params, meta)
        const b = conditionValue(names[1], params, meta)
        const same = String(isSet(a) ? a : '') === String(isSet(b) ? b : '')
        return equal ? same : !same
    }
    if (!pair(part.whenEqual, true) || !pair(part.whenNotEqual, false)) return false
    return matchesWhen(part.when, params, meta, paramDefs)
}

/**
 * Whether a `when` condition holds for this run. On a multiple param,
 * {param: [values]} means it includes any of them.
 */
export function matchesWhen(when, params, meta = {}, paramDefs = {}) {
    if (when === undefined || when === null || when === '') return true
    if (typeof when === 'string') return isSet(conditionValue(when, params, meta))
    return Object.keys(when).every(name => {
        const allowed = (Array.isArray(when[name]) ? when[name] : [when[name]]).map(a => (a === null ? '' : String(a)))
        const value = conditionValue(name, params, meta)
        if (!isSet(value)) return allowed.includes('')
        // A multiple param matches when it includes any of the values.
        const many = multipleValues(name, value, paramDefs)
        if (many) return many.some(v => allowed.includes(v))
        return allowed.includes(String(value))
    })
}

function isSet(value) {
    return value !== undefined && value !== null && value !== ''
}

/** A dot path into an object: 'meta.company' on {meta: {company}}. */
export function pathValue(source, path) {
    if (!source || !path) return undefined
    return String(path).split('.').reduce((obj, key) => (obj === null || obj === undefined ? undefined : obj[key]), source)
}

/**
 * A cell value as the printed column shows it: its `format` when it has one,
 * otherwise the plain value (a date as DateToStr).
 */
export function formatColumnValue(value, column) {
    if (value === null || value === undefined) return ''
    if (column && column.format) return formatWith(column.format, value, column.type === 'date' || column.type === 'datetime')
    if (column && (column.type === 'date' || column.type === 'datetime')) return dateToStr(value)
    return String(value)
}

function sumOf(rows, key) {
    return (rows || []).reduce((total, row) => total + (parseFloat(row[key]) || 0), 0)
}

/**
 * One parameter as the heading shows it.
 *
 * ctx.params:    the run's values by name (ReportRunner's ranWith).
 * ctx.paramDefs: parameter definitions by name, for the type.
 * ctx.options:   the chosen option object(s) by name, captured when the report ran.
 */
function renderParam(name, modifier, ctx) {
    const value = (ctx.params || {})[name]
    if (!isSet(value)) return ''

    const def = (ctx.paramDefs || {})[name] || {}

    if (!modifier) {
        if (def.type === 'date') return dateToStr(value)
        return String(value)
    }
    if (modifier === 'value') return String(value)
    // FrameTracker's SQL list text for a multiple pick: ('B','C','E').
    if (modifier === 'sqlList') {
        const join = def.join !== undefined && def.join !== null ? def.join : ','
        const values = def.multiple ? String(value).split(join).filter(v => v !== '') : [String(value)]
        return '(' + values.map(v => "'" + v + "'").join(',') + ')'
    }

    const chosen = (ctx.options || {})[name]
    const list = Array.isArray(chosen) ? chosen : (chosen ? [chosen] : [])
    if (!list.length) return modifier === 'label' ? String(value) : ''

    return list
        .map(opt => {
            const field = opt[modifier]
            if (field !== undefined && field !== null) return String(field)
            return modifier === 'label' ? String(opt.value) : ''
        })
        .filter(v => v !== '')
        .join(', ')
}

function renderPlaceholder(body, ctx) {
    const colon = body.indexOf(':')
    const head = colon === -1 ? body : body.slice(0, colon)
    const rest = colon === -1 ? '' : body.slice(colon + 1)
    const columns = ctx.columns || {}

    if (head === '_user' && !rest) return ctx.user ? String(ctx.user) : ''
    if (head === '_count' && !rest) {
        return formatWith('#,0', (ctx.rows || []).length)
    }
    if (head === 'row' && rest) {
        const row = ctx.row || (ctx.rows && ctx.rows[0])
        return row ? formatColumnValue(row[rest], columns[rest]) : ''
    }
    if (head === '_sum' && rest) {
        // Sums print in group footers: footerFormat, then totalFormat, then format.
        const column = columns[rest] || {}
        const total = sumOf(ctx.rows, rest)
        if (column.footerBlankWhenZero && total === 0) return ''
        const format = column.footerFormat || column.totalFormat || column.format
        return formatWith(format || '#,0.00', total)
    }
    if ((head === '_meta' || head === '_scope') && rest) {
        const v = pathValue(ctx.meta, head === '_scope' ? 'scope.' + rest : rest)
        return v === undefined || v === null || typeof v === 'object' ? '' : String(v)
    }
    if (head.charAt(0) === '_') return ''

    return renderParam(head, rest, ctx)
}

/** One string with its placeholders filled. */
export function fillPlaceholders(text, ctx) {
    return String(text).replace(/\{([A-Za-z0-9_.]+(?::[A-Za-z0-9_.]+)?)\}/g, (match, body) => renderPlaceholder(body, ctx))
}

/**
 * A whole template: a string, or a list of parts ({text, when} or plain strings)
 * joined with nothing between them, exactly as FrameTracker concatenates.
 */
export function renderTemplate(template, ctx = {}) {
    if (template === undefined || template === null || template === false) return ''
    const parts = Array.isArray(template) ? template : [template]
    return parts
        .map(part => {
            if (part === null || part === undefined) return ''
            if (typeof part === 'string' || typeof part === 'number') return fillPlaceholders(part, ctx)
            if (!partApplies(part, ctx.params || {}, ctx.meta || {}, ctx.paramDefs || {})) return ''
            return fillPlaceholders(part.text || '', ctx)
        })
        .join('')
}

/**
 * A line that is exactly one '{_meta:key}' placeholder whose meta value is
 * {blank: true}: a spacer line the report class supplies (export title rows).
 */
function metaBlank(t, ctx) {
    let text = t
    if (Array.isArray(t) && t.length === 1) text = t[0]
    if (text && typeof text === 'object' && !Array.isArray(text)) {
        if (!partApplies(text, ctx.params || {}, ctx.meta || {}, ctx.paramDefs || {})) return false
        text = text.text
    }
    const match = /^\{_meta:([A-Za-z0-9_.]+)\}$/.exec(String(text === undefined ? '' : text))
    if (!match) return false
    const value = pathValue(ctx.meta, match[1])
    return isBlankLine(value)
}

/** Whether a template is an intentional empty line: {blank: true}. */
function isBlankLine(t) {
    return !!t && typeof t === 'object' && !Array.isArray(t) && t.blank === true
}

/**
 * A list of lines (subheadings, export title rows). Each line is a template: a
 * string, one {text, when} part (a line printed only when it applies), or a list
 * of parts. A line that renders empty (every part omitted by its conditions, or
 * nothing to print) is left out; {blank: true} is an intentional empty line
 * (FrameTracker's Header.Add('')) and is always kept.
 */
export function renderLines(templates, ctx = {}) {
    if (!templates) return []
    const list = Array.isArray(templates) ? templates : [templates]
    const out = []
    list.forEach(t => {
        if (isBlankLine(t) || metaBlank(t, ctx)) {
            out.push('')
            return
        }
        const line = renderTemplate(t, ctx)
        if (line !== '') out.push(line)
    })
    return out
}
